import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wallet, RefreshCcw, Filter, Download, X, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { normalizeRole, ROLE_META } from "@/components/RoleBadge";
import { CHART_COLOR, CHART_AXIS } from "@/lib/chartPalette";
import GoalBarometer from "@/components/GoalBarometer";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";

const SCOPE_BY_ROLE = {
  basic: ["mine"],
  control: ["mine", "team"],
  master: ["mine", "all"],
  finanza: ["all"],
};

const STATUS_OPTIONS = [
  { key: "partially_paid", label: "Pagata (90%)", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { key: "released",       label: "Rilasciata (100%)", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "pending",        label: "Da pagare", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "lost",           label: "Rifiutata / Persa", cls: "bg-red-100 text-red-800 border-red-300" },
];

const PERIOD_PRESETS = [
  { key: "today",     label: "Heute" },
  { key: "week",      label: "Diese Woche" },
  { key: "month",     label: "Aktueller Monat" },
  { key: "last_month", label: "Letzter Monat" },
  { key: "year",      label: "Dieses Jahr" },
  { key: "all",       label: "Alle" },
  { key: "custom",    label: "Benutzerdefiniert" },
];

function Card({ label, value, hint, accent = "text-foreground", className = "", testId }) {
  return (
    <div className={`prime-card p-5 ${className}`} data-testid={testId}>
      <div className="label-uppercase text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold text-3xl sm:text-4xl mt-1 ${accent}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function CommissionStatusBadge({ status }) {
  const m = STATUS_OPTIONS.find(s => s.key === status) || STATUS_OPTIONS[2];
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${m.cls}`}>{m.label}</span>;
}

/* ----------------- Period helpers ----------------- */
function periodRange(preset) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  switch (preset) {
    case "today":
      start.setHours(0, 0, 0, 0); break;
    case "week": {
      const dow = (start.getDay() + 6) % 7; // Mon-based
      start.setDate(start.getDate() - dow);
      start.setHours(0, 0, 0, 0); break;
    }
    case "month":
      start.setDate(1); start.setHours(0, 0, 0, 0); break;
    case "last_month":
      start.setMonth(start.getMonth() - 1); start.setDate(1); start.setHours(0, 0, 0, 0);
      end.setDate(1); end.setHours(0, 0, 0, 0); end.setTime(end.getTime() - 1); break;
    case "year":
      start.setMonth(0); start.setDate(1); start.setHours(0, 0, 0, 0); break;
    case "all":
    default:
      return { from: null, to: null };
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-CH"); } catch { return iso; }
}

function rowDateForFilter(row, dateBasis) {
  if (dateBasis === "paid") return row.paid_at || row.released_at || row.created_at;
  return row.created_at;
}

/* ----------------- Filters bar ----------------- */
function FiltersBar({
  consultants, companies, products, scope, filters, setFilters, onReset, embedded = false,
}) {
  const wrapperCls = embedded
    ? "px-6 py-3 border-b border-border space-y-2 bg-secondary/30"
    : "prime-card p-4 space-y-3";
  return (
    <div className={wrapperCls} data-testid="commissions-filters">
      <div className="flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-prime" />
        <h3 className="font-display font-bold text-xs uppercase tracking-wider">Filter</h3>
        <button
          onClick={onReset}
          className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="filters-reset-btn"
        >
          <X className="w-3 h-3" /> Zurücksetzen
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Consultant — only relevant when scope shows multiple users */}
        {scope !== "mine" && (
          <div data-testid="filter-consultant">
            <label className="label-uppercase text-[10px] text-muted-foreground">Berater</label>
            <Select value={filters.consultantId} onValueChange={(v) => setFilters(f => ({ ...f, consultantId: v }))}>
              <SelectTrigger className="h-9 text-xs" data-testid="filter-consultant-trigger">
                <SelectValue placeholder="Alle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle Berater</SelectItem>
                {consultants.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div data-testid="filter-company">
          <label className="label-uppercase text-[10px] text-muted-foreground">Gesellschaft</label>
          <Select value={filters.company} onValueChange={(v) => setFilters(f => ({ ...f, company: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-company-trigger">
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Gesellschaften</SelectItem>
              {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div data-testid="filter-product">
          <label className="label-uppercase text-[10px] text-muted-foreground">Produkt</label>
          <Select value={filters.product} onValueChange={(v) => setFilters(f => ({ ...f, product: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-product-trigger">
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Produkte</SelectItem>
              {products.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div data-testid="filter-period">
          <label className="label-uppercase text-[10px] text-muted-foreground">Zeitraum</label>
          <Select value={filters.period} onValueChange={(v) => setFilters(f => ({ ...f, period: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-period-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div data-testid="filter-date-basis">
          <label className="label-uppercase text-[10px] text-muted-foreground">Datums-Basis</label>
          <Select value={filters.dateBasis} onValueChange={(v) => setFilters(f => ({ ...f, dateBasis: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="filter-date-basis-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created">Erstellung</SelectItem>
              <SelectItem value="paid">Zahlung</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Custom date range */}
      {filters.period === "custom" && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <Input
            type="date" value={filters.customFrom}
            onChange={(e) => setFilters(f => ({ ...f, customFrom: e.target.value }))}
            className="h-9 text-xs w-auto" data-testid="filter-custom-from"
          />
          <span className="text-xs text-muted-foreground">→</span>
          <Input
            type="date" value={filters.customTo}
            onChange={(e) => setFilters(f => ({ ...f, customTo: e.target.value }))}
            className="h-9 text-xs w-auto" data-testid="filter-custom-to"
          />
        </div>
      )}

      {/* Status pills (multi-select) */}
      <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="filter-status-group">
        <span className="label-uppercase text-[10px] text-muted-foreground mr-1">Status:</span>
        <button
          onClick={() => setFilters(f => ({ ...f, statuses: [] }))}
          className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full transition-colors ${
            filters.statuses.length === 0 ? "bg-foreground text-background" : "border border-border hover:border-foreground/40"
          }`}
          data-testid="filter-status-all"
        >
          Alle
        </button>
        {STATUS_OPTIONS.map(s => {
          const active = filters.statuses.includes(s.key);
          return (
            <button
              key={s.key}
              onClick={() => setFilters(f => ({
                ...f,
                statuses: active ? f.statuses.filter(x => x !== s.key) : [...f.statuses, s.key],
              }))}
              className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full border transition-colors ${
                active ? s.cls : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`filter-status-${s.key}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------- Mini timeline (12 months bars) ----------------- */
function MonthlyTimeline({ rows, dateBasis }) {
  const data = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("de-CH", { month: "short" });
      buckets.push({ key, label, gross: 0, paid: 0, year: d.getFullYear() });
    }
    const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
    rows.forEach(r => {
      const ds = rowDateForFilter(r, dateBasis);
      if (!ds) return;
      const d = new Date(ds);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (key in idx) {
        buckets[idx[key]].gross += r.gross_amount || 0;
        buckets[idx[key]].paid += r.paid_amount || 0;
      }
    });
    return buckets;
  }, [rows, dateBasis]);

  const max = Math.max(1, ...data.map(d => d.gross));

  return (
    <div className="prime-card p-5" data-testid="commissions-timeline">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="label-uppercase text-muted-foreground">Letzte 12 Monate</div>
          <h3 className="font-display font-bold text-lg">Produktion · {dateBasis === "paid" ? "Zahlungsdatum" : "Erstellungsdatum"}</h3>
        </div>
        <div className="text-xs text-muted-foreground">Brutto pro Monat</div>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 6, left: -8, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_AXIS.tick }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: CHART_AXIS.tick }} axisLine={false} tickLine={false}
                   tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
            <Tooltip
              cursor={{ fill: "rgba(178,135,45,0.08)" }}
              contentStyle={{ background: "#fff", border: `1px solid ${CHART_COLOR.SAND}`, borderRadius: 8, fontSize: 12 }}
              formatter={(v, name) => [`CHF ${fmtMoney(v)}`, name === "gross" ? "Brutto" : "Bezahlt"]}
              labelFormatter={(_, p) => p?.[0]?.payload ? `${p[0].payload.label} ${p[0].payload.year}` : ""}
            />
            <Bar dataKey="gross" radius={[4, 4, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.gross === max ? CHART_COLOR.GOLD_DARK : CHART_COLOR.GOLD} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ----------------- Page ----------------- */
export default function CommissionsPage() {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  const allowed = SCOPE_BY_ROLE[role] || ["mine"];
  const [scope, setScope] = useState(allowed[0] || "mine");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const [filters, setFilters] = useState({
    consultantId: "__all__",
    company: "__all__",
    product: "__all__",
    statuses: [],          // [] = all
    period: "month",       // default = current month
    customFrom: "",
    customTo: "",
    dateBasis: "created",  // 'created' | 'paid'
  });

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.get("/commissions", { params: { scope } });
      setRows(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  /* ----- Derived option lists from data ----- */
  const consultants = useMemo(() => {
    const seen = {};
    rows.forEach(r => {
      if (r.user_id && r.consultant_name && !seen[r.user_id]) {
        seen[r.user_id] = { id: r.user_id, name: r.consultant_name };
      }
    });
    return Object.values(seen).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const companies = useMemo(() => {
    const out = {};
    rows.forEach(r => { if (r.company_name) out[r.company_name] = true; });
    return Object.keys(out).sort();
  }, [rows]);
  const products = useMemo(() => {
    const out = {};
    rows.forEach(r => { if (r.insurance_type) out[r.insurance_type] = true; });
    return Object.keys(out).sort();
  }, [rows]);

  /* ----- Period bounds ----- */
  const periodBounds = useMemo(() => {
    if (filters.period === "custom") {
      const from = filters.customFrom ? new Date(filters.customFrom + "T00:00:00").toISOString() : null;
      const to = filters.customTo ? new Date(filters.customTo + "T23:59:59.999").toISOString() : null;
      return { from, to };
    }
    return periodRange(filters.period);
  }, [filters.period, filters.customFrom, filters.customTo]);

  /* ----- Filtered rows ----- */
  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filters.consultantId !== "__all__" && r.user_id !== filters.consultantId) return false;
      if (filters.company !== "__all__" && (r.company_name || "") !== filters.company) return false;
      if (filters.product !== "__all__" && (r.insurance_type || "") !== filters.product) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(r.status)) return false;
      const ds = rowDateForFilter(r, filters.dateBasis);
      if (periodBounds.from && (!ds || ds < periodBounds.from)) return false;
      if (periodBounds.to && (!ds || ds > periodBounds.to)) return false;
      return true;
    });
  }, [rows, filters, periodBounds]);

  /* ----- Totals based on filtered set ----- */
  const totals = useMemo(() => {
    const t = { gross: 0, paid: 0, storno: 0, released: 0, lost: 0, count: filtered.length };
    filtered.forEach(r => {
      t.gross += r.gross_amount || 0;
      t.paid += r.paid_amount || 0;
      if (r.status === "released") t.released += r.storno_amount || 0;
      else if (r.status === "lost") t.lost += (r.gross_amount || 0) - (r.paid_amount || 0);
      else t.storno += r.storno_amount || 0;
    });
    return t;
  }, [filtered]);

  const myTotals = useMemo(() => {
    const t = { gross: 0, paid: 0, storno: 0, count: 0 };
    filtered.filter(r => r.user_id === user?.id).forEach(r => {
      t.gross += r.gross_amount || 0;
      t.paid += r.paid_amount || 0;
      if (r.status !== "released" && r.status !== "lost") t.storno += r.storno_amount || 0;
      t.count += 1;
    });
    return t;
  }, [filtered, user]);

  const releaseDue = async () => {
    try {
      const res = await api.post("/commissions/release-due");
      toast.success(`${res.data.released} Provisionen rilasciate dal fondo`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const resetFilters = () => setFilters({
    consultantId: "__all__", company: "__all__", product: "__all__",
    statuses: [], period: "all", customFrom: "", customTo: "", dateBasis: "created",
  });

  /* ----- Export CSV (filtered) ----- */
  const exportCsv = () => {
    const isBasic = role === "basic";
    const header = ["Datum", "Kunde", "Gesellschaft", "Police-Nr.", "Produkt", "Berater", "Rolle",
      ...(isBasic ? [] : ["Tasso %"]),
      "Brutto CHF", "Bezahlt CHF", "Storno CHF", "Status"];
    const lines = [header.join(";")];
    filtered.forEach(r => {
      lines.push([
        fmtDate(rowDateForFilter(r, filters.dateBasis)),
        r.client_name || "",
        r.company_name || "",
        r.policy_number || "",
        r.insurance_type || "",
        r.consultant_name || "",
        r.role_at_time || "",
        ...(isBasic ? [] : [(r.rate_pct ?? 0).toString().replace(".", ",")]),
        (r.gross_amount ?? 0).toFixed(2).replace(".", ","),
        (r.paid_amount ?? 0).toFixed(2).replace(".", ","),
        (r.storno_amount ?? 0).toFixed(2).replace(".", ","),
        r.status || "",
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(";"));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `provisionen_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const meta = ROLE_META[role];

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className={`label-uppercase ${meta.text}`}>/ Provisionen · {meta.label}</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
            {role === "master" ? "Provisions-Cockpit" :
             role === "control" ? "Provisionen Team" :
             role === "finanza" ? "Globale Provisionen" : "Le mie Provisionen"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            90% bei Aktivierung bezahlt · 10% in Storno-Reserve · automatische Freigabe nach 3 Monaten bei aktiver Police.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {allowed.length > 1 && allowed.map(s => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                scope === s ? "bg-prime text-white" : "border border-border hover:border-prime/40"
              }`}
              data-testid={`scope-${s}`}
            >
              {s === "mine" ? "Meine" : s === "team" ? "Team" : "Unterschrift"}
            </button>
          ))}
          {role === "master" && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={releaseDue} data-testid="release-due-btn">
              <RefreshCcw className="w-3.5 h-3.5" /> Reife freigeben
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} disabled={filtered.length === 0} data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Personal monthly goal */}
      <GoalBarometer />

      {/* Role-specific summary (reactive to filters) */}
      {role === "basic" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="summary-basic">
          <Card label="Brutto" value={`CHF ${fmtMoney(myTotals.gross)}`} hint={`${myTotals.count} Dossiers`}
                accent="text-foreground" testId="card-basic-gross" />
          <Card label="Storno-Reserve" value={`CHF ${fmtMoney(myTotals.storno)}`} hint="10 % zurückbehalten"
                accent="text-amber-700" className="border-l-4 border-amber-400" testId="card-basic-storno" />
          <Card label="Netto bezahlt" value={`CHF ${fmtMoney(myTotals.paid)}`} hint="90% eingenommen"
                accent="text-green-700" className="border-l-4 border-green-500" testId="card-basic-net" />
        </div>
      )}
      {role === "control" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="summary-control">
          <Card label="Meine" value={`CHF ${fmtMoney(myTotals.gross)}`} accent="text-foreground" testId="card-control-mine" />
          <Card label="Team" value={`CHF ${fmtMoney(totals.gross - myTotals.gross)}`}
                accent="text-amber-700" className="border-l-4 border-amber-500" testId="card-control-team" />
          <Card label="Gesamt verwaltet" value={`CHF ${fmtMoney(totals.gross)}`}
                hint={`${totals.count} righe`} accent="text-prime" testId="card-control-total" />
        </div>
      )}
      {(role === "master" || role === "finanza") && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="summary-master">
          <Card label="Gesamtproduktion" value={`CHF ${fmtMoney(totals.gross)}`}
                hint={`${totals.count} Bewegungen`} accent="text-foreground" testId="card-m-gross" />
          <Card label="Bezahlt" value={`CHF ${fmtMoney(totals.paid + totals.released)}`}
                accent="text-green-700" className="border-l-4 border-green-500" testId="card-m-paid" />
          <Card label="Storno-Reserve" value={`CHF ${fmtMoney(totals.storno)}`}
                accent="text-amber-700" className="border-l-4 border-amber-500" testId="card-m-storno" />
          <Card label="Verloren (Storno)" value={`CHF ${fmtMoney(totals.lost)}`}
                accent="text-red-700" className="border-l-4 border-red-500" testId="card-m-lost" />
        </div>
      )}

      {/* Mini timeline */}
      <MonthlyTimeline rows={filtered} dateBasis={filters.dateBasis} />

      {/* Detail table — Filter + Bewegungen as one block */}
      <div className="prime-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <Wallet className="w-4 h-4 text-prime" />
          <h2 className="font-display font-bold text-xl">Bewegungen ({filtered.length}<span className="text-muted-foreground font-normal text-sm"> / {rows.length}</span>)</h2>
        </div>
        <FiltersBar
          embedded
          consultants={consultants}
          companies={companies}
          products={products}
          scope={scope}
          filters={filters}
          setFilters={setFilters}
          onReset={resetFilters}
        />
        {busy && rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Lädt…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {rows.length === 0
                ? "Keine Provision registriert."
                : "Keine Bewegung entspricht den aktuellen Filtern."}
            </p>
            {rows.length > 0 && (
              <button onClick={resetFilters} className="mt-3 text-xs text-prime hover:underline" data-testid="empty-reset-filters">
                Filter zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commissions-table">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Datum</th>
                  <th className="px-3 py-2 font-medium">Kunde</th>
                  <th className="px-3 py-2 font-medium">Gesellschaft</th>
                  <th className="px-3 py-2 font-medium">Police-Nr.</th>
                  <th className="px-3 py-2 font-medium">Produkt</th>
                  <th className="px-3 py-2 font-medium">Berater</th>
                  <th className="px-3 py-2 font-medium">Rolle</th>
                  {role !== "basic" && <th className="px-3 py-2 font-medium text-right">%</th>}
                  <th className="px-3 py-2 font-medium text-right">Brutto</th>
                  <th className="px-3 py-2 font-medium text-right">Bezahlt</th>
                  <th className="px-3 py-2 font-medium text-right">Storno</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-xs">
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-secondary/30" data-testid={`commission-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{fmtDate(rowDateForFilter(r, filters.dateBasis))}</td>
                    <td className="px-3 py-2 font-semibold">{r.client_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">{r.company_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{r.policy_number || "—"}</td>
                    <td className="px-3 py-2 capitalize">{r.insurance_type}</td>
                    <td className="px-3 py-2">{r.consultant_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ROLE_META[r.role_at_time]?.bgSoft || "bg-stone-100"} ${ROLE_META[r.role_at_time]?.text || "text-stone-700"}`}>
                        {r.role_at_time}
                      </span>
                    </td>
                    {role !== "basic" && (
                      <td className="px-3 py-2 text-right font-mono">
                        {r.rate_pct != null ? `${r.rate_pct}%` : "—"}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.gross_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-green-700">{fmtMoney(r.paid_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-700">{fmtMoney(r.storno_amount)}</td>
                    <td className="px-3 py-2"><CommissionStatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
